<script>
import {
    projectState
} from '@app/store/projectStore.svelte.js';
import {
    uiState
} from '@app/store/uiStore.svelte.js';
import { environment } from '@app/store/envStore.svelte.js';
import {authState} from '@app/store/authStore.svelte.js';
import { can } from "@app/lib/entitlements.js";
import { isQuickToolsPanelAllowed } from '@app/store/permissionStore.svelte.js';
import { untrack } from 'svelte';
import Summary from './Summary.svelte';
import Styles from './Styles.svelte';
import FindAndReplace from './FindAndReplace.svelte';
import SpellCheck from './SpellCheck.svelte';
import StyleGuide from './StyleGuide.svelte';
import TimingAndSync from './TimingAndSync.svelte';
import EventTemplates from './EventTemplates.svelte';
import Voices from './Voices.svelte';
import Speakers from './Speakers.svelte';
import Tags from './Tags.svelte';
import Markers from './Markers.svelte';
import Notes from './Notes.svelte';
import VideoFilters from './VideoFilters.svelte';
import ManualQc from './ManualQc.svelte';
import ErrorNav from './ErrorNav.svelte';
import LiveCaptioning from './LiveCaptioning.svelte';
import Media from './Media.svelte';
import * as m from "@app/paraglide/messages.js";

let canUseAudioDescription = $derived(authState.ad || !environment.online);
let canUseLiveCaptioning = $derived(
    environment.online &&
        can("liveCaptions"),
);
let canUseMediaTab = $derived(environment.electron && !environment.iframe);

// Tab visibility: the user's QuickTools settings, edition gates, and the
// session-only permission overlay set by an IFrame parent.
let show = $derived.by(() => {
    const qt = uiState.quickTools;
    const visible = {
        summary: qt.summary,
        styles: qt.styles,
        findAndReplace: qt.search,
        spellCheck: qt.spellCheck,
        timingAndSync: qt.timing,
        liveCaptioning: canUseLiveCaptioning && qt.liveCaptioning,
        qcAndReview: qt.qc,
        manualQc: qt.manualQc,
        errors: qt.errorNav,
        speakers: qt.speakers,
        tags: qt.tags,
        markers: qt.markers,
        notes: qt.notes,
        media: canUseMediaTab && qt.media,
        videoFilters: qt.videoFilters,
        eventTemplates: qt.templates,
        voices: canUseAudioDescription && qt.voices,
    };
    return Object.fromEntries(
        Object.entries(visible).map(([id, on]) => [id, Boolean(on) && isQuickToolsPanelAllowed(id)]),
    );
});

const FALLBACK_ORDER = ["summary", "findAndReplace", "spellCheck", "timingAndSync", "qcAndReview", "speakers"];

// Default to summary if available, otherwise fallback to other tools
function selectFallbackTool() {
    uiState.quickTools.selected =
        FALLBACK_ORDER.find((id) => show[id]) ??
        Object.keys(show).find((id) => show[id]) ??
        "styles";
}

$effect(() => {
    if (!show[uiState.quickTools.selected]) {
        untrack(selectFallbackTool);
    }
});

function updateSelectedTool(tool) {
    uiState.quickTools.selected = tool;
    if (uiState.quickTools.selected === "timingAndSync") {
        projectState.mode = "timing";
    } else {
        projectState.mode = "edit";
    }
}
</script>

<div class="row g-0 mt-2" id="QuickToolsWrapper">
    <div id="QuickTools" role="complementary" aria-label={m.quicktools_panel_label()}>
        <div>
            <ul id="QuickToolsNav" class="nav nav-tabs border-bottom-0" role="tablist" aria-label={m.quicktools_nav_label()}>
                {#if show.summary}
                <li class="nav-item" role="presentation">
                    <button type="button" id="summary-tab"
                        class="btn text-capitalize nav-link btn-link rounded-0 rounded-top {uiState.quickTools.selected === 'summary' ? 'active bg-light text-dark border-light' : ''}"
                        onclick={() => updateSelectedTool("summary")}
                        role="tab"
                        aria-controls="summary-panel"
                        aria-selected={uiState.quickTools.selected === 'summary'}>{m.quicktools_tab_info()}</button>
                </li>
                {/if}
                {#if show.styles}
                <li class="nav-item" role="presentation">
                    <button type="button" id="styles-tab"
                        class="btn text-capitalize nav-link btn-link rounded-0 rounded-top {uiState.quickTools.selected === 'styles' ? 'active bg-light text-dark border-light' : ''}"
                        onclick={() => updateSelectedTool("styles")}
                        role="tab"
                        aria-controls="styles-panel"
                        aria-selected={uiState.quickTools.selected === 'styles'}>{m.quicktools_tab_styles()}</button>
                </li>
                {/if}
                {#if show.findAndReplace}
                    <li class="nav-item" role="presentation">
                        <button type="button" id="search-tab"
                            class="btn text-capitalize nav-link btn-link rounded-0 rounded-top {uiState.quickTools.selected === 'findAndReplace' ? 'active bg-light text-dark border-light' : ''}"
                            onclick={() => updateSelectedTool("findAndReplace")}
                            role="tab"
                            aria-controls="search-panel"
                            aria-selected={uiState.quickTools.selected === 'findAndReplace'}>{m.quicktools_tab_search_replace()}</button>
                    </li>
                {/if}
                {#if show.spellCheck}
                    <li class="nav-item" role="presentation">
                        <button type="button" id="spellCheck-tab"
                            class="btn text-capitalize nav-link btn-link rounded-0 rounded-top {uiState.quickTools.selected === 'spellCheck' ? 'active bg-light text-dark border-light' : ''}"
                            onclick={() => updateSelectedTool("spellCheck")}
                            role="tab"
                            aria-controls="spellCheck-panel"
                            aria-selected={uiState.quickTools.selected === 'spellCheck'}>{m.quicktools_tab_spell_check()}</button>
                    </li>
                {/if}
                {#if show.timingAndSync}
                    <li class="nav-item" role="presentation">
                        <button id="timingSyncTab" type="button"
                            class="btn text-capitalize nav-link btn-link rounded-0 rounded-top {uiState.quickTools.selected === 'timingAndSync' ? 'active bg-light text-dark border-light' : ''}"
                            onclick={() => updateSelectedTool("timingAndSync")}
                            role="tab"
                            aria-controls="timingAndSync-panel"
                            aria-selected={uiState.quickTools.selected === 'timingAndSync'}>{m.quicktools_tab_timing_sync()}</button>
                    </li>
                {/if}
                {#if show.liveCaptioning}
                    <li class="nav-item" role="presentation">
                        <button type="button" id="liveCaptioning-tab"
                            class="btn text-capitalize nav-link btn-link rounded-0 rounded-top {uiState.quickTools.selected === 'liveCaptioning' ? 'active bg-light text-dark border-light' : ''}"
                            onclick={() => updateSelectedTool("liveCaptioning")}
                            role="tab"
                            aria-controls="liveCaptioning-panel"
                            aria-selected={uiState.quickTools.selected === 'liveCaptioning'}>{m.quicktools_tab_live_captioning()}</button>
                    </li>
                {/if}
                {#if show.qcAndReview}
                    <li class="nav-item" role="presentation">
                        <button type="button" id="qcAndReview-tab"
                            class="btn text-capitalize nav-link btn-link rounded-0 rounded-top {uiState.quickTools.selected === 'qcAndReview' ? 'active bg-light text-dark border-light' : ''}"
                            onclick={() => updateSelectedTool("qcAndReview")}
                            role="tab"
                            aria-controls="qcAndReview-panel"
                            aria-selected={uiState.quickTools.selected === 'qcAndReview'}>{m.quicktools_tab_qc_review()}</button>
                    </li>
                {/if}
                {#if show.manualQc}
                    <li class="nav-item" role="presentation">
                        <button type="button" id="manualQc-tab"
                            class="btn text-capitalize nav-link btn-link rounded-0 rounded-top {uiState.quickTools.selected === 'manualQc' ? 'active bg-light text-dark border-light' : ''}"
                            onclick={() => updateSelectedTool("manualQc")}
                            role="tab"
                            aria-controls="manualQc-panel"
                            aria-selected={uiState.quickTools.selected === 'manualQc'}>{m.quicktools_tab_manual_qc()}</button>
                    </li>
                {/if}
                {#if show.errors}
                    <li class="nav-item" role="presentation">
                        <button type="button" id="errorNav-tab"
                            class="btn text-capitalize nav-link btn-link rounded-0 rounded-top {uiState.quickTools.selected === 'errors' ? 'active bg-light text-dark border-light' : ''}"
                            onclick={() => updateSelectedTool("errors")}
                            role="tab"
                            aria-controls="errorNav-panel"
                            aria-selected={uiState.quickTools.selected === 'errors'}>{m.quicktools_tab_errors()}</button>
                    </li>
                {/if}
                {#if show.speakers}
                    <li class="nav-item" role="presentation">
                        <button type="button" id="speakers-tab"
                            class="btn text-capitalize nav-link btn-link rounded-0 rounded-top {uiState.quickTools.selected === 'speakers' ? 'active bg-light text-dark border-light' : ''}"
                            onclick={() => updateSelectedTool("speakers")}
                            role="tab"
                            aria-controls="speakers-panel"
                            aria-selected={uiState.quickTools.selected === 'speakers'}>{m.quicktools_tab_speakers()}</button>
                    </li>
                {/if}
                {#if show.tags}
                    <li class="nav-item" role="presentation">
                        <button type="button" id="tags-tab"
                            class="btn text-capitalize nav-link btn-link rounded-0 rounded-top {uiState.quickTools.selected === 'tags' ? 'active bg-light text-dark border-light' : ''}"
                            onclick={() => updateSelectedTool("tags")}
                            role="tab"
                            aria-controls="tags-panel"
                            aria-selected={uiState.quickTools.selected === 'tags'}>{m.quicktools_tab_tags()}</button>
                    </li>
                {/if}
                {#if show.markers}
                    <li class="nav-item" role="presentation">
                        <button type="button" id="markers-tab"
                            class="btn text-capitalize nav-link btn-link rounded-0 rounded-top {uiState.quickTools.selected === 'markers' ? 'active bg-light text-dark border-light' : ''}"
                            onclick={() => updateSelectedTool("markers")}
                            role="tab"
                            aria-controls="markers-panel"
                            aria-selected={uiState.quickTools.selected === 'markers'}>{m.quicktools_tab_markers()}</button>
                    </li>
                {/if}
                {#if show.notes}
                    <li class="nav-item" role="presentation">
                        <button type="button" id="notes-tab"
                            class="btn text-capitalize nav-link btn-link rounded-0 rounded-top {uiState.quickTools.selected === 'notes' ? 'active bg-light text-dark border-light' : ''}"
                            onclick={() => updateSelectedTool("notes")}
                            role="tab"
                            aria-controls="notes-panel"
                            aria-selected={uiState.quickTools.selected === 'notes'}>{m.quicktools_tab_notes()}</button>
                    </li>
                {/if}
                {#if show.media}
                    <li class="nav-item" role="presentation">
                        <button type="button" id="media-tab"
                            class="btn text-capitalize nav-link btn-link rounded-0 rounded-top {uiState.quickTools.selected === 'media' ? 'active bg-light text-dark border-light' : ''}"
                            onclick={() => updateSelectedTool("media")}
                            role="tab"
                            aria-controls="media-panel"
                            aria-selected={uiState.quickTools.selected === 'media'}>{m.quicktools_tab_media()}</button>
                    </li>
                {/if}
                {#if show.videoFilters}
                    <li class="nav-item" role="presentation">
                        <button type="button" id="videoFilters-tab"
                            class="btn text-capitalize nav-link btn-link rounded-0 rounded-top {uiState.quickTools.selected === 'videoFilters' ? 'active bg-light text-dark border-light' : ''}"
                            onclick={() => updateSelectedTool("videoFilters")}
                            role="tab"
                            aria-controls="videoFilters-panel"
                            aria-selected={uiState.quickTools.selected === 'videoFilters'}>{m.quicktools_tab_video_filters()}</button>
                    </li>
                {/if}
                {#if show.eventTemplates}
                    <li class="nav-item" role="presentation">
                        <button type="button" id="eventTemplates-tab"
                            class="btn text-capitalize nav-link btn-link rounded-0 rounded-top {uiState.quickTools.selected === 'eventTemplates' ? 'active bg-light text-dark border-light' : ''}"
                            onclick={() => updateSelectedTool("eventTemplates")}
                            role="tab"
                            aria-controls="eventTemplates-panel"
                            aria-selected={uiState.quickTools.selected === 'eventTemplates'}>{m.quicktools_tab_event_templates()}</button>
                    </li>
                {/if}
                {#if show.voices}
                    <li class="nav-item" role="presentation">
                        <button type="button" id="voices-tab"
                            class="btn text-capitalize nav-link btn-link rounded-0 rounded-top {uiState.quickTools.selected === 'voices' ? 'active bg-light text-dark border-light' : ''}"
                            onclick={() => updateSelectedTool("voices")}
                            role="tab"
                            aria-controls="voices-panel"
                            aria-selected={uiState.quickTools.selected === 'voices'}>{m.quicktools_tab_voices()}</button>
                    </li>
                {/if}
            </ul>
            <div class="tab-content p-2 border border-top-0 border-light shadow rounded-bottom">
                <div id="summary-panel" role="tabpanel" aria-labelledby="summary-tab" class:hidden={uiState.quickTools.selected !== "summary"}>
                    {#if uiState.quickTools.selected === "summary" && show.summary}
                        <Summary />
                    {/if}
                </div>
                <div id="styles-panel" role="tabpanel" aria-labelledby="styles-tab" class:hidden={uiState.quickTools.selected !== "styles"}>
                    {#if uiState.quickTools.selected === "styles" && show.styles}
                        <Styles />
                    {/if}
                </div>
                <div id="search-panel" role="tabpanel" aria-labelledby="search-tab" class:hidden={uiState.quickTools.selected !== "findAndReplace"}>
                    {#if uiState.quickTools.selected === "findAndReplace" && show.findAndReplace}
                        <FindAndReplace />
                    {/if}
                </div>
                <div id="spellCheck-panel" role="tabpanel" aria-labelledby="spellCheck-tab" class:hidden={uiState.quickTools.selected !== "spellCheck"}>
                    {#if uiState.quickTools.selected === "spellCheck" && show.spellCheck}
                        <SpellCheck/>
                    {/if}
                </div>
                <div id="timingAndSync-panel" role="tabpanel" aria-labelledby="timingAndSync-tab" class:hidden={uiState.quickTools.selected !== "timingAndSync"}>
                    {#if uiState.quickTools.selected === "timingAndSync" && show.timingAndSync}
                        <TimingAndSync></TimingAndSync>
                    {/if}
                </div>
                <div id="liveCaptioning-panel" role="tabpanel" aria-labelledby="liveCaptioning-tab" class:hidden={uiState.quickTools.selected !== "liveCaptioning"}>
                    {#if uiState.quickTools.selected === "liveCaptioning" && show.liveCaptioning}
                        <LiveCaptioning />
                    {/if}
                </div>
                <div id="qcAndReview-panel" role="tabpanel" aria-labelledby="qcAndReview-tab" class:hidden={uiState.quickTools.selected !== "qcAndReview"}>
                    {#if uiState.quickTools.selected === "qcAndReview" && show.qcAndReview}
                        <StyleGuide></StyleGuide>
                    {/if}
                </div>
                <div id="manualQc-panel" role="tabpanel" aria-labelledby="manualQc-tab" class:hidden={uiState.quickTools.selected !== "manualQc"}>
                    {#if uiState.quickTools.selected === "manualQc" && show.manualQc}
                        <ManualQc></ManualQc>
                    {/if}
                </div>
                <div id="errorNav-panel" role="tabpanel" aria-labelledby="errorNav-tab" class:hidden={uiState.quickTools.selected !== "errors"}>
                    {#if uiState.quickTools.selected === "errors" && show.errors}
                        <ErrorNav></ErrorNav>
                    {/if}
                </div>
                <div id="speakers-panel" role="tabpanel" aria-labelledby="speakers-tab" class:hidden={uiState.quickTools.selected !== "speakers"}>
                    {#if uiState.quickTools.selected === "speakers" && show.speakers}
                        <Speakers></Speakers>
                    {/if}
                </div>
                <div id="tags-panel" role="tabpanel" aria-labelledby="tags-tab" class:hidden={uiState.quickTools.selected !== "tags"}>
                    {#if uiState.quickTools.selected === "tags" && show.tags}
                        <Tags></Tags>
                    {/if}
                </div>
                <div id="markers-panel" role="tabpanel" aria-labelledby="markers-tab" class:hidden={uiState.quickTools.selected !== "markers"}>
                    {#if uiState.quickTools.selected === "markers" && show.markers}
                        <Markers></Markers>
                    {/if}
                </div>
                <div id="notes-panel" role="tabpanel" aria-labelledby="notes-tab" class:hidden={uiState.quickTools.selected !== "notes"}>
                    {#if uiState.quickTools.selected === "notes" && show.notes}
                        <Notes></Notes>
                    {/if}
                </div>
                <div id="videoFilters-panel" role="tabpanel" aria-labelledby="videoFilters-tab" class:hidden={uiState.quickTools.selected !== "videoFilters"}>
                    {#if uiState.quickTools.selected === "videoFilters" && show.videoFilters}
                        <VideoFilters></VideoFilters>
                    {/if}
                </div>
                <div id="eventTemplates-panel" role="tabpanel" aria-labelledby="eventTemplates-tab" class:hidden={uiState.quickTools.selected !== "eventTemplates"}>
                    {#if uiState.quickTools.selected === "eventTemplates" && show.eventTemplates}
                        <EventTemplates></EventTemplates>
                    {/if}
                </div>
                <div id="voices-panel" role="tabpanel" aria-labelledby="voices-tab" class:hidden={uiState.quickTools.selected !== "voices"}>
                    {#if uiState.quickTools.selected === "voices" && show.voices}
                        <Voices></Voices>
                    {/if}
                </div>
                <div id="media-panel" role="tabpanel" aria-labelledby="media-tab" class:hidden={uiState.quickTools.selected !== "media"}>
                    {#if uiState.quickTools.selected === "media" && show.media}
                        <Media />
                    {/if}
                </div>
            </div>
        </div>
    </div>
</div>

<style lang="css">
#QuickToolsNav {
    overflow-x: scroll;
    overflow-y: hidden;
    flex-wrap: nowrap;
    display: -webkit-box;
    will-change: transform;
}
.hidden {
    display: none;
}
</style>
